import React, { useEffect, useMemo, useState } from 'react';
import L from 'leaflet';
import { CircleMarker, MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { Button } from 'react-bootstrap';
import { Layers, X } from 'lucide-react';
import { PlacedBuilding } from '../../../hooks/analysis/useRoutes';
import { clockOf, PlaybackFrame } from '../utils/routes';
import { VisibleRoute } from './RouteStopList';

export interface SelectedStop {
    uid: string;
    index: number;
}

/** A person's day being replayed */
export interface Replay {
    uid: string;
    color: string;
    running: boolean;
    frame: PlaybackFrame;
}

interface RoutesMapProps {
    routes: VisibleRoute[];
    placedBuildings: Map<string, PlacedBuilding>;
    selectedStop: SelectedStop | null;
    onSelectStop: (stop: SelectedStop) => void;
    replay: Replay | null;
    onStopReplay: () => void;
}

const SWEDEN_CENTER: [number, number] = [59.33, 18.07];
const FOCUS_ZOOM = 15;
const MAX_ZOOM = 19;

const position = (building: PlacedBuilding): [number, number] =>
    [building.coordinates.latitude, building.coordinates.longitude];

// Cached so a replay's frames don't rebuild every marker. Content is a
// number and a colour of our own, never user text.
const iconCache = new Map<string, L.DivIcon>();
const stopIcon = (color: string, number: number, selected: boolean): L.DivIcon => {
    const key = `${color}|${number}|${selected}`;
    let icon = iconCache.get(key);
    if (!icon) {
        icon = L.divIcon({
            className: 'routes-marker',
            html: `<span class="routes-marker-dot${selected ? ' selected' : ''}" style="background-color:${color}">${number}</span>`,
            iconSize: [28, 28],
            iconAnchor: [14, 14],
        });
        iconCache.set(key, icon);
    }
    return icon;
};

/** Fits the map to the shown stops whenever they change */
const FitToStops: React.FC<{ points: [number, number][] }> = ({ points }) => {
    const map = useMap();
    const signature = points.map(p => p.join(',')).join(';');
    useEffect(() => {
        if (points.length === 0) return;
        if (points.length === 1) map.setView(points[0], FOCUS_ZOOM);
        else map.fitBounds(L.latLngBounds(points), { padding: [32, 32] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, signature]);
    return null;
};

/** Pans to the picked stop; every pick is a new point, so picking the same stop again pans back to it */
const PanToStop: React.FC<{ point: [number, number] | null }> = ({ point }) => {
    const map = useMap();
    useEffect(() => {
        if (point) map.flyTo(point, Math.max(map.getZoom(), FOCUS_ZOOM));
    }, [map, point]);
    return null;
};

const RoutesMap: React.FC<RoutesMapProps> = ({ routes, placedBuildings, selectedStop, onSelectStop, replay, onStopReplay }) => {
    const [isSatellite, setIsSatellite] = useState(false);

    const lines = useMemo(() => routes.map(route => ({
        route,
        points: route.stops.flatMap(stop => {
            const building = placedBuildings.get(stop.buildingKey);
            return building ? [position(building)] : [];
        }),
    })), [routes, placedBuildings]);

    const allPoints = useMemo(() => lines.flatMap(line => line.points), [lines]);

    const selectedPoint = useMemo(() => {
        if (!selectedStop) return null;
        const stop = routes.find(route => route.uid === selectedStop.uid)?.stops[selectedStop.index];
        const building = stop && placedBuildings.get(stop.buildingKey);
        return building ? position(building) : null;
    }, [selectedStop, routes, placedBuildings]);

    return (
        <div className="routes-map-frame">
            <MapContainer center={SWEDEN_CENTER} zoom={6} maxZoom={MAX_ZOOM} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
                {/* Keyed so a switch builds a new layer with its own zoom limits */}
                {isSatellite ? (
                    <TileLayer
                        key="satellite"
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
                        maxZoom={MAX_ZOOM}
                        maxNativeZoom={18}
                    />
                ) : (
                    <TileLayer
                        key="map"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        maxZoom={MAX_ZOOM}
                        maxNativeZoom={19}
                    />
                )}
                <FitToStops points={allPoints} />
                <PanToStop point={selectedPoint} />
                {/* During a replay the other people fade and the replayed route shows dashed, filling in as the day goes */}
                {lines.map(({ route, points }) => {
                    const replayed = replay?.uid === route.uid;
                    const pathOptions = !replay
                        ? { color: route.color, weight: 4, opacity: 0.8 }
                        : replayed
                            ? { color: route.color, weight: 3, opacity: 0.35, dashArray: '6 8' }
                            : { color: route.color, weight: 3, opacity: 0.15 };
                    return <Polyline key={`line-${route.uid}`} positions={points} pathOptions={pathOptions} />;
                })}
                {replay && (
                    <>
                        <Polyline positions={replay.frame.path} pathOptions={{ color: replay.color, weight: 5, opacity: 0.95 }} />
                        <CircleMarker
                            center={replay.frame.point}
                            radius={9}
                            pathOptions={{ color: '#fff', weight: 3, fillColor: replay.color, fillOpacity: 1 }}
                        />
                    </>
                )}
                {lines.map(({ route }) => route.stops.map((stop, index) => {
                    const building = placedBuildings.get(stop.buildingKey);
                    if (!building) return null;
                    if (replay && (replay.uid !== route.uid || index >= replay.frame.reachedStops)) return null;
                    const selected = selectedStop?.uid === route.uid && selectedStop.index === index;
                    return (
                        <Marker
                            key={`${route.uid}-${stop.buildingKey}`}
                            position={position(building)}
                            icon={stopIcon(route.color, index + 1, selected)}
                            zIndexOffset={selected ? 1000 : 0}
                            eventHandlers={{ click: () => onSelectStop({ uid: route.uid, index }) }}
                        >
                            <Tooltip direction="top" offset={[0, -14]}>
                                {index + 1}. {building.name} ({route.name})
                            </Tooltip>
                        </Marker>
                    );
                }))}
            </MapContainer>
            {replay && (
                <div className="routes-replay-clock" role="status">
                    <span className="routes-swatch" style={{ backgroundColor: replay.color }} />
                    <span className="routes-replay-time">{clockOf(replay.frame.seconds)}</span>
                    <button type="button" className="routes-replay-close" aria-label="Avsluta uppspelningen" onClick={onStopReplay}>
                        <X size={18} />
                    </button>
                </div>
            )}
            <Button
                className="routes-layer-toggle"
                variant="light"
                aria-label={isSatellite ? 'Visa karta' : 'Visa satellitbild'}
                onClick={() => setIsSatellite(prev => !prev)}
            >
                <Layers size={20} />
            </Button>
        </div>
    );
};

export default RoutesMap;
